import { useState, type ImgHTMLAttributes } from 'react';
import { ImageIcon } from 'lucide-react';

/**
 * Whitelisted widths — must stay in sync with `ALLOWED_RESIZE_WIDTHS` in
 * `@ah/cloud/storage`. Adding a value is a one-line change in both places.
 */
const ALLOWED_WIDTHS = [64, 128, 192, 256, 384, 512, 768, 1024, 1536, 1920] as const;

/**
 * Match the canonical asset URL: `${origin}/{uuid}/original[.ext]`. Captures
 * the origin (group 1) and the uuid (group 2) so we can rewrite the URL in
 * place — the resize endpoint lives at `${origin}/r/{uuid}?w=…` on the
 * same host the source asset came from, so dev/staging/prod all "just work"
 * without an env var.
 */
const ASSET_URL_RE =
  /^(https?:\/\/[^/]+)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/original\b/i;

function pickServerWidth(renderWidth: number): number {
  const dpr =
    typeof window !== 'undefined'
      ? Math.min(Math.max(window.devicePixelRatio || 1, 1), 3)
      : 2;
  const target = Math.ceil(renderWidth * dpr);
  for (const w of ALLOWED_WIDTHS) if (w >= target) return w;
  return ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1]!;
}

export interface ResizedImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null | undefined;
  /**
   * Logical render width in CSS pixels (the size the image will be displayed
   * at). The component multiplies by DPR (capped at 3) and picks the smallest
   * allowed server-side width that satisfies it.
   */
  renderWidth: number;
  /** Show a muted fallback tile when src is missing/errors. Default true. */
  fallback?: boolean;
  /** Icon size for the fallback tile. Default 18. */
  fallbackIconSize?: number;
}

/**
 * `<img>` that routes asset-bucket URLs through the dynamic resizer at
 * `/img/:id?w=…`. Non-asset URLs (e.g. `/logo-mark.png` from each app's
 * `public/` dir, or external hotlinks) pass through unchanged.
 *
 * Picks the smallest server width that covers `renderWidth × devicePixelRatio`.
 * If the URL doesn't match the canonical `{uuid}/original.*` shape, the
 * original src is used as-is — safe by default.
 */
export function ResizedImage({
  src,
  renderWidth,
  fallback = true,
  fallbackIconSize = 18,
  className = '',
  alt = '',
  ...rest
}: ResizedImageProps) {
  const [errored, setErrored] = useState(false);

  if (!src || errored) {
    if (!fallback) return null;
    return (
      <div
        aria-hidden={alt ? undefined : true}
        className={`bg-muted text-muted-foreground/50 flex items-center justify-center ${className}`}
      >
        <ImageIcon size={fallbackIconSize} strokeWidth={1.25} />
      </div>
    );
  }

  const match = src.match(ASSET_URL_RE);
  const finalSrc = match
    ? `${match[1]}/r/${match[2]}?w=${pickServerWidth(renderWidth)}`
    : src;

  return (
    <img
      {...rest}
      src={finalSrc}
      alt={alt}
      className={className}
      onError={(e) => {
        setErrored(true);
        rest.onError?.(e);
      }}
    />
  );
}
